import { create } from 'zustand'

/**
 * Monotonic version counter that ticks every time the four repository
 * singletons are re-bound (local → cloud on sign-in, or back to local on
 * sign-out). Hooks that hold their own snapshot of repo data — or that
 * front a Zustand store whose `initialize()` is gated on an `isInitialized`
 * flag — subscribe to this version in their effect deps so a binding flip
 * triggers a refetch against the now-different backing repo.
 *
 * **Store-backed facade hooks need this too** (`useDebts`,
 * `useRecurringExpenses`). v0.8.24 originally documented stores as exempt
 * because `bindReposToSession.resetStores()` wipes their in-memory state.
 * That's necessary but insufficient: the facade `useEffect(() => { void
 * initialize() }, [initialize])` has a stable Zustand action reference,
 * so it does NOT re-fire after `reset()` flips `isInitialized` back to
 * false. The fresh-page-load + warm-session race on Chrome tab-restore
 * (v0.8.35) made this load-bearing — the rebind wiped the store after
 * the initial render and nothing re-fetched. Adding `repoVersion` to the
 * effect deps closes it.
 *
 * `useLedger` is the exception that proves the rule: its effect already
 * depends on `[debtsInitialized, isInitialized, ...]`, so the chain
 * naturally re-fires once `useDebts` re-initialises.
 *
 * The audit (v0.8.21 deferred items) flagged the recovery-anchor staleness:
 * "Dashboard Recovery Progress headline is wrong until refresh." Root
 * cause was `useRecoveryAnchor`'s initial-load effect had `[]` deps and
 * never re-fired after `bindReposToSession` swapped the anchor repo.
 * Subscribing to `repoVersion` closes the gap without forcing the anchor
 * into a Zustand store of its own.
 */
interface RepoVersionState {
  version: number
  bumpRepoVersion: () => void
}

export const useRepoVersionStore = create<RepoVersionState>()((set) => ({
  version: 0,
  bumpRepoVersion: () => set((s) => ({ version: s.version + 1 })),
}))

/** Read-only hook for components/hooks that just want the current version. */
export function useRepoVersion(): number {
  return useRepoVersionStore((s) => s.version)
}
